/**
 * MoveLearnersCard — "Is Poltergeist an Elite TM move?" (decisions/0016 slice 2).
 *
 * Elite TM status is per Pokémon–move pair, so the card shows the split the
 * backend's ``move_learners_card`` carries: the Pokémon that need an Elite TM
 * for the move, then those that learn it normally.  Rendered by CardRenderer.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }

function NameList({ names }: { names: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {names.map((n) => (
        <span key={n} className="px-2 py-0.5 rounded-full bg-white/[0.06] text-xs text-white/80">{n}</span>
      ))}
    </div>
  );
}

export default function MoveLearnersCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "learners") as Record<string, unknown> | undefined;
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">No move data available.</p>
      </CardShell>
    );
  }
  const elite = Array.isArray(sec.elite) ? (sec.elite as string[]) : [];
  const regular = Array.isArray(sec.regular) ? (sec.regular as string[]) : [];
  const regularCount = typeof sec.regular_count === "number" ? sec.regular_count : regular.length;
  const topPvp = Array.isArray(sec.top_pvp) ? (sec.top_pvp as string[]) : [];
  const pokeType = typeof sec.poke_type === "string" ? sec.poke_type : "";
  const badgeClass = TYPE_BADGE_COLORS[pokeType.toLowerCase()] ?? "bg-white/10 text-white/60";
  const category = sec.move_type === "fast" ? "Fast" : sec.move_type === "charge" ? "Charged" : null;
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {pokeType && <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badgeClass}`}>{pokeType}</span>}
          {category && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/40">{category}</span>}
        </div>
        {topPvp.length > 0 && (
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">Best in PvP</p>
            <NameList names={topPvp} />
          </div>
        )}
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">Needs an Elite TM</p>
          {elite.length > 0 ? <NameList names={elite} /> : <p className="text-sm text-white/50">No Pokémon needs an Elite TM for this move.</p>}
        </div>
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">
            Learns it normally{regularCount > regular.length ? ` · ${regularCount} in all` : ""}
          </p>
          {regular.length > 0 ? <NameList names={regular} /> : <p className="text-sm text-white/50">No Pokémon learns it without an Elite TM.</p>}
        </div>
      </div>
    </CardShell>
  );
}
